import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Shield, Home as HomeIcon, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroData, programsData } from '../data/mock';

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    document.title = "David's Harmony House Foundation Inc. - Transforming Lives";
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % heroData.images.length
      );
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const iconMap = {
    Shield: Shield,
    Heart: Heart,
    Users: Users,
    Home: HomeIcon
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {heroData.images.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt={`Community support ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50"></div>
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 rounded-full text-orange-300 text-sm font-medium">
                {heroData.subtitle}
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {heroData.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl leading-relaxed">
              {heroData.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/get-help">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Get Help Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/get-involved">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold text-lg px-8 py-6 backdrop-blur-sm transition-all duration-300"
                >
                  Get Involved
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {heroData.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-orange-400 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-300 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-6 h-6 text-white rotate-90" />
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Building a <span className="text-orange-600">Continuum of Care</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              We partner with Orange County Government Florida, the US Department of Veterans Administration, 
              and community faith-based organizations to create sustainable pathways of restoration and 
              economic empowerment for those who need it most.
            </p>
            <Link to="/about">
              <Button variant="outline" className="border-2 border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white font-semibold">
                Learn Our Story
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-orange-600">Core Programs</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four comprehensive programs addressing critical community needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {programsData.map((program, index) => {
              const IconComponent = iconMap[program.icon];
              return (
                <Card 
                  key={program.id} 
                  className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={program.image}
                      alt={program.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="p-2 bg-orange-500 rounded-lg">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-white">
                          {program.shortName}
                        </h3>
                      </div>
                      <p className="text-sm text-orange-300 font-medium">
                        {program.tagline}
                      </p>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {program.description}
                    </p>
                    <Link to={`/programs/${program.id}`}>
                      <Button 
                        variant="ghost" 
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 font-semibold group/btn p-0"
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                View All Programs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Together, We Create Lasting Change
            </h2>
            <p className="text-xl mb-8 text-orange-100 leading-relaxed">
              Every donation, every volunteer hour, every partnership contributes to 
              transforming lives and building stronger communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/donate">
                <Button 
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-gray-100 font-semibold text-lg px-8 shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Make a Donation
                  <Heart className="ml-2 w-5 h-5" fill="currentColor" />
                </Button>
              </Link>
              <Link to="/get-involved">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-orange-600 font-semibold text-lg px-8 transition-all duration-300"
                >
                  Volunteer With Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Banner */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-3">In Partnership With</p>
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm font-medium text-gray-300">
              <span>Orange County Government Florida</span>
              <span className="text-gray-600">•</span>
              <span>US Department of Veterans Administration</span>
              <span className="text-gray-600">•</span>
              <span>J K Awesome Deeds LLC</span>
              <span className="text-gray-600">•</span>
              <span>Community Faith-Based Organizations</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
